import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
} from '@mui/material';
import {
  Upload,
  Description,
  Analytics,
  Refresh,
  Download,
  Visibility,
  ExpandMore,
  CheckCircle,
  Warning,
  Info,
  PlayArrow,
  CloudUpload,
  Assignment,
  Psychology,
  Add,
  MoreHoriz,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { DropzoneArea } from 'mui-file-dropzone';
import { useSnackbar } from 'notistack';
import { documentProcessingApi } from '../services/documentProcessingApi';
import { LoggerService } from '../services/LoggerService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index} style={{ paddingTop: '24px' }}>
    {value === index && children}
  </div>
);

interface Document {
  id: string;
  title: string;
  document_type: string;
  publication_date?: string;
  effective_date?: string;
  language: string;
  summary?: string;
  is_processed: boolean;
  file_path?: string;
  processing_info?: {
    [key: string]: {
      processed_at: string;
      confidence: number;
    };
  };
  created_at?: string;
  updated_at?: string;
}

interface ProcessingJob {
  document_id: string;
  processing_type: string;
  status: 'processing' | 'completed' | 'failed';
  progress: number;
  job_id?: string;
  error?: string;
}

interface DocumentInsights {
  document_id: string;
  document_title: string;
  insights: {
    extract?: any;
    classify?: any;
    summarize?: any;
    analyze?: any;
  };
  last_updated?: string;
}

// Removed unused function getSeverityIcon

const getConfidenceColor = (confidence: number) => {
  if (confidence >= 90) return 'success';
  if (confidence >= 70) return 'warning';
  return 'error';
};

const AutomatedDocumentProcessing: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Theme removed as unused
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [documents, setDocuments] = useState<Document[]>([]);
  const [processingJobs, setProcessingJobs] = useState<ProcessingJob[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  
  // Dialog states
  const [uploadDialog, setUploadDialog] = useState(false);
  const [processDialog, setProcessDialog] = useState(false);
  const [insightsDialog, setInsightsDialog] = useState(false);
  const [bulkProcessDialog, setBulkProcessDialog] = useState(false);
  
  // Selected document and insights
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [documentInsights, setDocumentInsights] = useState<DocumentInsights | null>(null);
  
  // Form states
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [processingType, setProcessingType] = useState('analyze');
  const [modelName, setModelName] = useState('gpt-4');
  
  // Statistics
  const [statistics, setStatistics] = useState({
    total_documents: 0,
    processed_documents: 0,
    processing_rate: 0,
    recent_processed: 0
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load documents and statistics
      await Promise.all([
        loadDocuments(),
        loadStatistics()
      ]);
    } catch (error) {
      LoggerService.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const documents = await documentProcessingApi.getDocuments();
      setDocuments(documents);
    } catch (error) {
      LoggerService.error('Error loading documents:', error);
      enqueueSnackbar('Failed to load documents', { variant: 'error' });
      setDocuments([]);
    }
  };


  const loadStatistics = async () => {
    try {
      const stats = await documentProcessingApi.getProcessingStatistics();
      setStatistics({
        total_documents: stats.total_documents,
        processed_documents: stats.processed_documents,
        processing_rate: stats.success_rate,
        recent_processed: stats.processed_documents
      });
    } catch (error) {
      LoggerService.error('Error loading statistics:', error);
      enqueueSnackbar('Failed to load statistics', { variant: 'error' });
    }
  };

  const handleUploadFiles = async () => {
    if (uploadFiles.length === 0) return;
    
    setLoading(true);
    try {
      // Upload each file using the API
      for (const file of uploadFiles) {
        await documentProcessingApi.uploadDocument(file, (progress) => {
          LoggerService.debug(`Upload progress for ${file.name}: ${progress}%`);
        });
      }
      
      enqueueSnackbar(`Successfully uploaded ${uploadFiles.length} document(s)`, { variant: 'success' });
      setUploadFiles([]);
      setUploadDialog(false);
      
      // Reload documents list
      await loadDocuments();
    } catch (error) {
      LoggerService.error('Error uploading files:', error);
      enqueueSnackbar('Failed to upload documents', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleProcessDocument = async (document: Document) => {
    setLoading(true);
    try {
      const result = await documentProcessingApi.processDocument({
        document_id: document.id,
        processing_type: [processingType],
        ai_model: modelName
      });
      
      enqueueSnackbar('Document processing initiated', { variant: 'info' });
      
      // Create processing job for UI tracking
      const jobId = result.task_id || result.job_id || Date.now().toString();
      const job: ProcessingJob = {
        document_id: document.id,
        processing_type: processingType,
        status: 'processing',
        progress: 0,
        job_id: jobId
      };
      
      setProcessingJobs(prev => [...prev, job]);
      
      // Poll for status updates
      const pollStatus = async () => {
        try {
          const status = await documentProcessingApi.getProcessingStatus(job.job_id);
          
          // Update job status in UI
          setProcessingJobs(prev => 
            prev.map(j => 
              j.job_id === job.job_id 
                ? { 
                    ...j, 
                    status: status.status as 'processing' | 'completed' | 'failed',
                    progress: status.progress || 0,
                    error: status.error
                  }
                : j
            )
          );
          
          if (status.status === 'completed') {
            enqueueSnackbar('Document processed successfully', { variant: 'success' });
            await loadDocuments();
          } else if (status.status === 'failed') {
            enqueueSnackbar(`Processing failed: ${status.error || 'Unknown error'}`, { variant: 'error' });
          } else if (status.status === 'processing') {
            // Continue polling
            setTimeout(pollStatus, 2000);
          }
        } catch (error) {
          LoggerService.error('Error polling status:', error);
          enqueueSnackbar('Error checking processing status', { variant: 'error' });
        }
      };
      
      // Start polling after a short delay
      setTimeout(pollStatus, 2000);
      
      setProcessDialog(false);
    } catch (error: any) {
      LoggerService.error('Error processing document:', error);
      enqueueSnackbar(`Failed to process document: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkProcess = async () => {
    if (selectedDocuments.length === 0) return;
    
    setLoading(true);
    try {
      const result = await documentProcessingApi.processBulkDocuments({
        document_ids: selectedDocuments,
        processing_type: [processingType],
        ai_model: modelName
      });
      
      enqueueSnackbar(`Bulk processing initiated for ${selectedDocuments.length} document(s)`, { variant: 'info' });
      
      // Create processing jobs for UI tracking
      selectedDocuments.forEach((docId, index) => {
        const job: ProcessingJob = {
          document_id: docId,
          processing_type: processingType,
          status: 'processing',
          progress: 0,
          job_id: result.task_id ? `${result.task_id}_${index}` : `bulk_${Date.now()}_${docId}`
        };
        setProcessingJobs(prev => [...prev, job]);
      });
      
      // Poll for status updates if task_id is provided
      const taskId = result.task_id || result.job_id;
      if (taskId) {
        const pollStatus = async () => {
          try {
            const status = await documentProcessingApi.getProcessingStatus(taskId);
            
            if (status.status === 'completed') {
              enqueueSnackbar('Bulk processing completed', { variant: 'success' });
              await loadDocuments();
            } else if (status.status === 'failed') {
              enqueueSnackbar(`Bulk processing failed: ${status.error || 'Unknown error'}`, { variant: 'error' });
            } else if (status.status === 'processing') {
              // Update all jobs progress
              setProcessingJobs(prev =>
                prev.map(job => 
                  selectedDocuments.includes(job.document_id)
                    ? { ...job, progress: status.progress || 0 }
                    : job
                )
              );
              setTimeout(pollStatus, 2000);
            }
          } catch (error) {
            LoggerService.error('Error polling bulk status:', error);
          }
        };
        
        setTimeout(pollStatus, 2000);
      }
      
      setBulkProcessDialog(false);
      setSelectedDocuments([]);
    } catch (error: any) {
      LoggerService.error('Error in bulk processing:', error);
      enqueueSnackbar(`Failed to initiate bulk processing: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewInsights = async (document: Document) => {
    if (!document.is_processed) return;
    
    setLoading(true);
    try {
      // Fetch real insights from API
      const insights = await documentProcessingApi.getDocumentInsights(document.id);
      
      setDocumentInsights(insights);
      setSelectedDocument(document);
      setInsightsDialog(true);
    } catch (error) {
      LoggerService.error('Error loading insights:', error);
      enqueueSnackbar('Failed to load document insights', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderDocumentList = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Processing Statistics</Typography>
            <Button startIcon={<Refresh />} onClick={loadStatistics}>
              Refresh
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{statistics.total_documents}</Typography>
                  <Typography color="textSecondary">Total Documents</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{statistics.processed_documents}</Typography>
                  <Typography color="textSecondary">Processed</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{statistics.processing_rate.toFixed(1)}%</Typography>
                  <Typography color="textSecondary">Processing Rate</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{statistics.recent_processed}</Typography>
                  <Typography color="textSecondary">Recent (30d)</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Regulatory Documents</Typography>
            <Box>
              {selectedDocuments.length > 0 && (
                <Button
                  startIcon={<MoreHoriz />}
                  onClick={() => setBulkProcessDialog(true)}
                  sx={{ mr: 1 }}
                >
                  Bulk Process ({selectedDocuments.length})
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setUploadDialog(true)}
              >
                Upload Documents
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDocuments(documents.map(d => d.id));
                        } else {
                          setSelectedDocuments([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Document</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Publication Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Processing Info</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedDocuments.includes(document.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDocuments(prev => [...prev, document.id]);
                          } else {
                            setSelectedDocuments(prev => prev.filter(id => id !== document.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {document.title}
                        </Typography>
                        {document.summary && (
                          <Typography variant="caption" color="textSecondary">
                            {document.summary.substring(0, 100)}...
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={document.document_type} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {document.publication_date ? 
                        format(parseISO(document.publication_date), 'MMM dd, yyyy') : 
                        '-'
                      }
                    </TableCell>
                    <TableCell>
                      {document.is_processed ? (
                        <Chip 
                          label="Processed" 
                          color="success" 
                          size="small"
                          icon={<CheckCircle />}
                        />
                      ) : (
                        <Chip 
                          label="Pending" 
                          color="warning" 
                          size="small"
                          icon={<Warning />}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {document.processing_info && (
                        <Box>
                          {Object.entries(document.processing_info).map(([type, info]) => (
                            <Chip
                              key={type}
                              label={`${type}: ${info.confidence.toFixed(0)}%`}
                              size="small"
                              color={getConfidenceColor(info.confidence)}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        {!document.is_processed ? (
                          <Button
                            size="small"
                            startIcon={<PlayArrow />}
                            onClick={() => {
                              setSelectedDocument(document);
                              setProcessDialog(true);
                            }}
                          >
                            Process
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleViewInsights(document)}
                          >
                            View Insights
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderProcessingQueue = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Processing Queue
        </Typography>
      </Grid>

      {processingJobs.length === 0 ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              No processing jobs in queue
            </Typography>
          </Paper>
        </Grid>
      ) : (
        processingJobs.map((job) => {
          const document = documents.find(d => d.id === job.document_id);
          return (
            <Grid item xs={12} key={job.job_id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">
                        {document?.title || 'Unknown Document'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Processing Type: {job.processing_type}
                      </Typography>
                    </Box>
                    <Chip
                      label={job.status}
                      color={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'error' : 'info'}
                    />
                  </Box>
                  {job.status === 'processing' && (
                    <Box sx={{ width: '100%' }}>
                      <LinearProgress variant="indeterminate" />
                    </Box>
                  )}
                  {job.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {job.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })
      )}
    </Grid>
  );

  const renderUploadDialog = () => (
    <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>Upload Regulatory Documents</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <DropzoneArea
            onChange={(files) => setUploadFiles(files)}
            acceptedFiles={['.pdf', '.docx', '.doc', '.txt']}
            maxFileSize={52428800} // 50MB
            filesLimit={10}
            dropzoneText="Drag and drop regulatory documents here or click to browse"
            showFileNames={true}
            showAlerts={false}
            fileObjects={[]}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
        <Button
          onClick={handleUploadFiles}
          variant="contained"
          disabled={uploadFiles.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Upload />}
        >
          {loading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderProcessDialog = () => (
    <Dialog open={processDialog} onClose={() => setProcessDialog(false)}>
      <DialogTitle>Process Document</DialogTitle>
      <DialogContent>
        {selectedDocument && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {selectedDocument.title}
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Processing Type</InputLabel>
              <Select
                value={processingType}
                label="Processing Type"
                onChange={(e) => setProcessingType(e.target.value)}
              >
                <MenuItem value="extract">Extract Key Information</MenuItem>
                <MenuItem value="classify">Classify Document</MenuItem>
                <MenuItem value="summarize">Generate Summary</MenuItem>
                <MenuItem value="analyze">Compliance Analysis</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>AI Model</InputLabel>
              <Select
                value={modelName}
                label="AI Model"
                onChange={(e) => setModelName(e.target.value)}
              >
                <MenuItem value="gpt-4">GPT-4 (Most Accurate)</MenuItem>
                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Faster)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setProcessDialog(false)}>Cancel</Button>
        <Button
          onClick={() => selectedDocument && handleProcessDocument(selectedDocument)}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <PlayArrow />}
        >
          {loading ? 'Processing...' : 'Start Processing'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderInsightsDialog = () => (
    <Dialog open={insightsDialog} onClose={() => setInsightsDialog(false)} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Psychology sx={{ mr: 1 }} />
          AI Document Insights
        </Box>
      </DialogTitle>
      <DialogContent>
        {documentInsights && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {documentInsights.document_title}
            </Typography>
            
            {Object.entries(documentInsights.insights).map(([type, data]) => (
              <Accordion key={type} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                    {type} Results
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {type === 'analyze' && data && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Impact Level: 
                        <Chip 
                          label={data.impact_level} 
                          color={data.impact_level === 'high' ? 'error' : 'warning'}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Affected Business Areas:
                      </Typography>
                      <List dense>
                        {data.affected_business_areas?.map((area: string, index: number) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <Assignment fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={area} />
                          </ListItem>
                        ))}
                      </List>
                      
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Recommended Actions:
                      </Typography>
                      <List dense>
                        {data.recommended_actions?.map((action: string, index: number) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <CheckCircle fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText primary={action} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {type === 'summarize' && data && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Executive Summary:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {data.executive_summary}
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Key Changes:
                      </Typography>
                      <List dense>
                        {data.key_changes?.map((change: string, index: number) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <Info fontSize="small" color="info" />
                            </ListItemIcon>
                            <ListItemText primary={change} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setInsightsDialog(false)}>Close</Button>
        <Button startIcon={<Download />}>Export Report</Button>
      </DialogActions>
    </Dialog>
  );

  const renderBulkProcessDialog = () => (
    <Dialog open={bulkProcessDialog} onClose={() => setBulkProcessDialog(false)}>
      <DialogTitle>Bulk Process Documents</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Process {selectedDocuments.length} selected documents
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Processing Type</InputLabel>
            <Select
              value={processingType}
              label="Processing Type"
              onChange={(e) => setProcessingType(e.target.value)}
            >
              <MenuItem value="extract">Extract Key Information</MenuItem>
              <MenuItem value="classify">Classify Documents</MenuItem>
              <MenuItem value="summarize">Generate Summaries</MenuItem>
              <MenuItem value="analyze">Compliance Analysis</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>AI Model</InputLabel>
            <Select
              value={modelName}
              label="AI Model"
              onChange={(e) => setModelName(e.target.value)}
            >
              <MenuItem value="gpt-4">GPT-4 (Most Accurate)</MenuItem>
              <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Faster)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setBulkProcessDialog(false)}>Cancel</Button>
        <Button
          onClick={handleBulkProcess}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <PlayArrow />}
        >
          {loading ? 'Processing...' : 'Start Bulk Processing'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Automated Document Processing
      </Typography>
      
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Description />} label="Document Library" />
          <Tab icon={<Assignment />} label="Processing Queue" />
          <Tab icon={<Analytics />} label="Analytics" />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        {renderDocumentList()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderProcessingQueue()}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Typography variant="h5">Processing Analytics</Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          Detailed analytics will be implemented in the next phase.
        </Typography>
      </TabPanel>

      {/* Upload Dialog */}
      {renderUploadDialog()}
      
      {/* Process Dialog */}
      {renderProcessDialog()}
      
      {/* Insights Dialog */}
      {renderInsightsDialog()}
      
      {/* Bulk Process Dialog */}
      {renderBulkProcessDialog()}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="upload"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setUploadDialog(true)}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default AutomatedDocumentProcessing;